/**
 * `<FollowUpSection>` — a section of its own, not a Plan subsection, because the
 * closing order is Medical Decision Making → Total Time → Follow-Up. The items
 * are stripped off the tail of `summary.plan` on hydrate
 * (`stripFollowUpBlockFromPlan`) so they never render twice.
 */
import { ActionIcon, Box, Button, Group, Stack, Text, Textarea } from '@mantine/core'
import { IconPlus, IconX } from '@tabler/icons-react'
import { useFormContext, useWatch } from 'react-hook-form'
import type { UseFieldArrayReturn } from 'react-hook-form'
import type { ProgressNoteEditFormValues } from '../progressNoteEditSchema'
import { PROGRESS_NOTE_CORAL as CORAL } from '../tokens'
import { NoteHeading } from './NoteHeading'

export interface FollowUpSectionProps {
  readOnly: boolean
  followUpsField: UseFieldArrayReturn<ProgressNoteEditFormValues, 'followUps'>
}

export function FollowUpSection({ readOnly, followUpsField }: FollowUpSectionProps) {
  const { control, setValue } = useFormContext<ProgressNoteEditFormValues>()
  const followUps = useWatch({ control, name: 'followUps' }) || []

  if (readOnly && followUps.length === 0) return null

  return (
    <Box mb="xl">
      <NoteHeading level="section" id="followup" label="Follow-Up">
        <Text component="span" c="dimmed" size="xs" fw={400}>
          LOINC 18776-5
        </Text>
      </NoteHeading>
      {followUps.length > 0 && (
        <Stack gap="xs">
          {followUps.map((f, idx) => (
            <Group key={f.id} justify="space-between" wrap="nowrap" align="flex-start">
              <Group gap={10} align="flex-start" wrap="nowrap" style={{ flex: 1 }}>
                <Box
                  style={{
                    width: 7,
                    height: 7,
                    borderRadius: '50%',
                    background: CORAL,
                    flexShrink: 0,
                    marginTop: readOnly ? 7 : 11,
                  }}
                />
                {readOnly ? (
                  <Text size="sm" c="dark.7" style={{ whiteSpace: 'pre-wrap', lineHeight: 1.65 }}>
                    {f.text}
                  </Text>
                ) : (
                  <Textarea
                    size="xs"
                    autosize
                    minRows={1}
                    style={{ flex: 1 }}
                    value={f.text}
                    onChange={(e) =>
                      setValue(`followUps.${idx}.text`, e.currentTarget.value, {
                        shouldDirty: true,
                      })
                    }
                    placeholder="e.g. Schedule lipid panel in 3 months"
                  />
                )}
              </Group>
              {!readOnly && (
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  size="sm"
                  onClick={() => followUpsField.remove(idx)}
                  aria-label="Remove follow-up"
                >
                  <IconX size={14} />
                </ActionIcon>
              )}
            </Group>
          ))}
        </Stack>
      )}
      {!readOnly && (
        <Button
          size="xs"
          variant="light"
          color="blue"
          mt="sm"
          leftSection={<IconPlus size={14} />}
          onClick={() => followUpsField.append({ id: crypto.randomUUID(), text: '' })}
          data-testid="plan-add-followup"
        >
          Add Follow-Up Item
        </Button>
      )}
    </Box>
  )
}
